
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import SimpleErrorBoundary from './components/SimpleErrorBoundary.tsx'
import './types/globals.d.ts'

// Environment detection
const isProduction = window.location.hostname === 'phcsnmi.org';
const isDevelopment = window.location.hostname.includes('localhost');

// Google Analytics functions with environment awareness
function sendPageView(path: string): void {
  if (typeof window.gtag === 'function') {
    const cookieDomain = isProduction ? 'phcsnmi.org' : 'auto';
    
    console.log('📈 Sending page view:', { 
      path, 
      cookieDomain, 
      hostname: window.location.hostname,
      environment: isProduction ? 'production' : 'development'
    });
    
    window.gtag('config', 'G-Q7385RKSKJ', {
      page_path: path,
      cookie_domain: cookieDomain,
      debug_mode: !isProduction
    });
  } else {
    console.warn('⚠️ Google Analytics not loaded');
  }
}

// Track custom events with improved structure
const trackEvent = (category: string, action: string, label?: string, value?: number) => {
  if (window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      send_to: 'G-Q7385RKSKJ'
    });
    // Event tracked
  }
};

// Export the tracking function for use in other components
window.trackEvent = trackEvent;

// Add explicit domain verification event
if (window.gtag) {
  window.gtag('event', 'domain_verification', {
    event_category: 'Site_Verification',
    event_label: 'phcsnmi.org',
    non_interaction: true,
    send_to: 'G-Q7385RKSKJ'
  });
  // Domain verification event sent
}

// Add pageview tracking to history state changes
const originalPushState = history.pushState;
history.pushState = function(state, title, url) {
  originalPushState.apply(this, [state, title, url]);
  if (url) {
    sendPageView(url.toString());
  }
};

const originalReplaceState = history.replaceState;
history.replaceState = function(state, title, url) {
  originalReplaceState.apply(this, [state, title, url]);
  if (url) {
    sendPageView(url.toString());
  }
};

// Track initial page load explicitly
window.addEventListener('load', () => {
  sendPageView(window.location.pathname);
});

// Also track DOMContentLoaded for earlier verification
document.addEventListener('DOMContentLoaded', () => {
  sendPageView(window.location.pathname);
});

// Enhanced startup logging with environment detection
console.log('🚀 Application starting...', {
  timestamp: new Date().toISOString(),
  url: window.location.href,
  hostname: window.location.hostname,
  userAgent: navigator.userAgent.substring(0, 100),
  environment: import.meta.env.MODE,
  build: import.meta.env.DEV ? 'development' : 'production',
  isProduction,
  isDevelopment,
  viteVersion: import.meta.env.VITE_VERSION || 'unknown'
});

// Production error monitoring with detailed logging
if (isProduction || !isDevelopment) {
  let errorCount = 0;
  const MAX_ERRORS = 10;

  // Enhanced global error handler
  window.addEventListener('error', (event) => {
    errorCount++;
    const errorInfo = {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      errorCount
    };
    
    console.error('❌ Global error:', errorInfo);
    
    // Store error for debugging
    try {
      const errors = JSON.parse(localStorage.getItem('app_errors') || '[]');
      errors.push(errorInfo);
      if (errors.length > MAX_ERRORS) errors.shift();
      localStorage.setItem('app_errors', JSON.stringify(errors));
    } catch (e) {
      console.warn('Failed to store error:', e);
    }
  });

  // Enhanced promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    errorCount++;
    const rejectionInfo = {
      reason: event.reason?.toString() || 'Unknown rejection',
      stack: event.reason?.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      errorCount
    };
    
    console.error('❌ Unhandled promise rejection:', rejectionInfo);
    
    // Store rejection for debugging
    try {
      const rejections = JSON.parse(localStorage.getItem('app_rejections') || '[]');
      rejections.push(rejectionInfo);
      if (rejections.length > MAX_ERRORS) rejections.shift();
      localStorage.setItem('app_rejections', JSON.stringify(rejections));
    } catch (e) {
      console.warn('Failed to store rejection:', e);
    }
  });
}

// Production-ready React App initialization
const rootElement = document.getElementById("root");

if (!rootElement) {
  console.error('❌ Root element not found! Creating fallback...');
  const fallbackRoot = document.createElement('div');
  fallbackRoot.id = 'root';
  fallbackRoot.innerHTML = `
    <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; font-family: system-ui; background: #fafafa;">
      <div style="text-align: center; max-width: 500px; padding: 2rem; background: white; border-radius: 0.5rem; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
        <h1 style="color: #dc2626; margin-bottom: 1rem; font-size: 1.5rem;">Prince Hall Community Service Network</h1>
        <p style="margin-bottom: 2rem; color: #6b7280;">The application could not initialize properly. Please try refreshing the page.</p>
        <button onclick="window.location.reload()" style="background: #2563eb; color: white; padding: 0.75rem 1.5rem; border: none; border-radius: 0.375rem; cursor: pointer; font-weight: 500;">
          Refresh Page
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(fallbackRoot);
} else {
  console.log('✅ Root element found, initializing React app...');
}

const root = createRoot(rootElement || document.getElementById("root")!);

// Initialize React app with enhanced error handling
try {
  root.render(
    <SimpleErrorBoundary>
      <App />
    </SimpleErrorBoundary>
  );
  
  console.log('✅ React app rendered successfully');
  
  // Health check
  setTimeout(() => {
    const appElement = document.querySelector('[data-testid="app"]') || document.querySelector('#root > *');
    if (appElement) {
      console.log('✅ App health check passed - content is rendered');
    } else {
      console.warn('⚠️ App health check failed - no content detected');
    }
  }, 1000);
  
} catch (error) {
  console.error('❌ Failed to render React app:', error);
  
  // Fallback rendering
  const fallbackContent = `
    <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; font-family: system-ui; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);">
      <div style="text-align: center; max-width: 500px; padding: 2rem; background: white; border-radius: 1rem; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);">
        <h1 style="color: #dc2626; margin-bottom: 1rem; font-size: 1.5rem; font-weight: 700;">Initialization Error</h1>
        <p style="margin-bottom: 2rem; color: #6b7280;">Unable to start the application. Please refresh the page or contact support.</p>
        <button onclick="window.location.reload()" style="background: #2563eb; color: white; padding: 0.75rem 1.5rem; border: none; border-radius: 0.5rem; cursor: pointer; font-weight: 500;">
          Refresh Page
        </button>
      </div>
    </div>
  `;
  
  if (rootElement) {
    rootElement.innerHTML = fallbackContent;
  }
}
